import { CustomEpic, PayloadAction } from 'types/types';
import {
  makePaymentFailure,
  makePaymentSuccess,
  PaymentPayload,
} from 'modules/payment/actions';
import { ofType } from 'redux-observable';
import { PAYMENT_START } from 'modules/payment/actionTypes';
import { exhaustMap, mergeMap, switchMap } from 'rxjs/operators';
import { from, of, pipe } from 'rxjs';
import { getStripe } from 'utils/stripePromise';
import { handleError } from 'utils/epics/handleError';

export const makePayment$: CustomEpic<PaymentPayload> = action$ =>
  action$.pipe(
    ofType(PAYMENT_START),
    // `exhaustMap`, not `switchMap`: while a card is being confirmed, a second
    //   `PAYMENT_START` is ignored rather than replacing the first.
    //
    // Cancelling a read discards a stale answer. Cancelling this discards the
    //   result of a charge that has already been sent to Stripe, so the charge
    //   that happened never reaches `makePaymentSuccess` — and the confirmation
    //   is issued a second time on top of it.
    //
    // `TriggerCardPayment` no longer dispatches twice, which is the actual fix.
    //   This is the layer under it, so no future caller can reintroduce it.
    //
    // The trade-off, stated because it is not free: if a confirmation never
    //   settles — `confirmCardPayment` returns a promise nobody can cancel —
    //   this ignores every later `PAYMENT_START` for the life of the session,
    //   so "Try again" does nothing until the app is reloaded. Under
    //   `switchMap` a retry would have gone through.
    //
    // That is the outcome to want, though, not a cost to regret. A confirmation
    //   still hanging is a charge whose state is *unknown*, and issuing a second
    //   one against an unknown first is exactly the thing being prevented. The
    //   screen already says "Payment in progress...", which is true.
    //
    // Create an inner observable to protect the epic from errors
    exhaustMap((params: PayloadAction<PaymentPayload>) =>
      of(params).pipe(
        switchMap(async action => {
          const { payload } = action;
          const stripe = await getStripe();
          if (!stripe) {
            throw new Error('Stripe not found');
          }
          return {
            payload,
            stripe,
          };
        }),
        mergeMap(
          ({ payload: { paymentIntentSecret, paymentMethodId }, stripe }) =>
            from(
              stripe.confirmCardPayment(paymentIntentSecret, {
                payment_method: paymentMethodId,
                // Allows us to re-use the card with this customer
                // https://stripe.com/docs/payments/save-during-payment?platform=web#web-submit-payment
                setup_future_usage: 'off_session',
              }),
            ),
        ),
        mergeMap(({ paymentIntent, error }) => {
          if (paymentIntent) {
            return of(makePaymentSuccess({ paymentIntent }));
          }
          return of(
            makePaymentFailure({
              name: error?.code || 'Payment failed',
              message: error?.message,
            }),
          );
        }),
        pipe(handleError(makePaymentFailure)),
      ),
    ),
  );
